import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const makeCode = (n = 5) => Array.from({ length: n }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join("");

export default function Captcha({ onValid, testid = "captcha", label = "Type the code shown" }) {
  const [code, setCode] = useState(makeCode);
  const [val, setVal] = useState("");
  const canvasRef = useRef(null);
  const validRef = useRef(onValid);
  validRef.current = onValid;

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#eef2ff";
    ctx.fillRect(0, 0, W, H);
    for (let i = 0; i < 6; i++) {
      ctx.strokeStyle = `rgba(0,82,255,${0.12 + Math.random() * 0.15})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * W, Math.random() * H);
      ctx.lineTo(Math.random() * W, Math.random() * H);
      ctx.stroke();
    }
    const colors = ["#0052FF", "#182678", "#1d4ed8", "#2563eb"];
    code.split("").forEach((ch, i) => {
      ctx.save();
      const x = 16 + i * ((W - 24) / code.length);
      const y = H / 2 + (Math.random() * 8 - 4);
      ctx.translate(x, y);
      ctx.rotate((Math.random() * 0.5 - 0.25));
      ctx.font = `bold ${26 + Math.random() * 6}px "JetBrains Mono", monospace`;
      ctx.fillStyle = colors[i % colors.length];
      ctx.fillText(ch, 0, 10);
      ctx.restore();
    });
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(24,38,120,${Math.random() * 0.3})`;
      ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
    }
  }, [code]);

  useEffect(() => { draw(); }, [draw]);
  useEffect(() => { validRef.current?.(val.trim().toUpperCase() === code); }, [val, code]);
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search.includes("e2e=1")) window[`__captcha_${testid}`] = code;
  }, [code, testid]);

  const refresh = () => { setCode(makeCode()); setVal(""); };

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="flex items-center gap-2">
        <canvas ref={canvasRef} width={150} height={48} className="rounded-lg border border-slate-200 bg-indigo-50" data-testid={`${testid}-canvas`} aria-label="CAPTCHA image" />
        <button type="button" onClick={refresh} className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 btn-press" data-testid={`${testid}-refresh`} aria-label="Refresh code"><RefreshCw className="h-4 w-4" /></button>
        <Input className="h-11 flex-1 uppercase tracking-widest" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Enter code" maxLength={6} data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}
